import { FormGroup } from '@angular/forms';
import { PolicyApiModels } from './api/policy-api.models';
import { AutomobileRiskModels } from './automobile-risk.models';
import { AvenantModels } from './avenant.models';
import { CommonModels } from './common.models';
import { DocumentModels } from './document.models';
import { HealthModels } from './health.models';
import { PaymentModels } from './payment.models';
import { PropertyChange } from './property-change';
import { QuotationModels } from './quotation.models';
import { TableModels } from './table.models';
import HealthAvenantType = CommonModels.HealthAvenantType;

export interface HeanthAvenantInfo {
  fileUrl: string;
  created: string;
  effectDate: string;
  type: HealthAvenantType;
}

export namespace PolicyModels {
  import TerminationTypes = PolicyApiModels.TerminationTypes;
  import Resiliation = HealthModels.Resilisation;

  export interface PatchPolicy {
    creatorAccountId?: number;
  }
  export interface AvenantRecapRequest {
    risk: AutomobileRiskModels.Form;
    formulaChoice: QuotationModels.FormulaChoice;
    loading?: number;
    effectDate: string;
    avenantTypes: AvenantModels.AvenantTypeAll[];
  }

  export interface ContractSummary {
    ageoId: number;
    subStatus: SubStatus;
    agencyId: number;
    id: number;
    clientId: number;
    publicId: string;
    agency: string;
    effectDate: Date;
    creationDate: Date;
    state: PolicyStateEnum;
  }

  export interface Policy {
    ageoId?: number;
    contractSigned: boolean;
    isResilNotCancellable: boolean;
    isTempoNotCancellable: boolean;
    isActivationNotCancellable: boolean;
    isCaduciteNotCancellable: boolean;
    isLastAvenantNotCancellable: boolean;
    isNulliteNotCancellable: boolean;
    id: number;
    publicId: string;
    insuranceType: CommonModels.InsuranceTypeEnum;
    formula: QuotationModels.FormulaEnum;
    creationDate: Date;
    effectDate: Date;
    staggering: QuotationModels.QuittancementEnum;
    status: PolicyStateEnum;
    monthsForFirstPayment: number;
    packageId: PackageEnum;
    optionalWarranties: QuotationModels.WarrantyEnum[];
    currentYear: PolicyYear;
    stateCreationDate: Date;
    termination?: PolicyTermination;
    caducite?: CaducitePayload;
    nullite?: NullitePayload;
    finGarantie?: FinGarantieProvisoirePayload;
    renewal: PolicyRenewal;
    quantieme?: number;
    isVad?: boolean;
    isRappel?: boolean;
    ria: Resiliation;
    controlStatus?: PolicyJobStatusEnum;
    instanceReasonId?: PolicyJobStatusEnum;
    assignedTo?: string;
    assignationDate?: Date;
    reminder1Date?: string;
    reminder2Date?: string;
    instanceCreationDate?: Date;
    parent?: ContractSummary;
    childs?: ContractSummary[];
    avenants?: HeanthAvenantInfo[];
    hasCvFv?: boolean;
  }

  export interface PolicyRenewal {
    effectDate: Date;
    renewalNoticeUrl: string;
    state: PolicyModels.RenewalStateEnum;
    rejectCode?: number;
  }

  export interface IPolicyStateExtraData {}

  export interface CaducitePayload extends IPolicyStateExtraData {
    caduciteType: CaduciteTypeEnum;
  }

  export interface NullitePayload extends IPolicyStateExtraData {
    nulliteType: NulliteTypeEnum;
  }

  export interface FinGarantieProvisoirePayload extends IPolicyStateExtraData {
    reason: FinGarantieProvisoireTypeEnum;
    effectDate: Date;
  }

  export interface FinGarantieProvisoirePayload extends IPolicyStateExtraData {
    reason: FinGarantieProvisoireTypeEnum;
    effectDate: Date;
  }

  export interface CaduciteRequest {
    type: CaduciteTypeEnum;
  }

  export interface NulliteRequest {
    type: CaduciteTypeEnum;
  }

  export interface PolicyTermination {
    type: TerminationTypes;
    effectDate: Date;
    paymentLimitDate?: Date;
  }

  export interface QuantiemeUpdateRequest {
    dayOfMonth: number;
  }

  export interface PayableReceipts {
    total: number;
    receipts: PayableReceipt[];
    paymentOrder: PaymentModels.PaymentOrderResume;
  }

  export interface PayableReceipt {
    id: number;
    receiptNumber: number;
    order: number;
    type: PolicyModels.ReceiptTypeEnum;
    startDate: Date;
    endDate: Date;
    amount: number;
    required: boolean;
  }

  export interface PolicyYear {
    year: number;
    startDate: Date;
    endDate: Date;
  }

  export interface Receipt {
    receiptNumber: number;
    receiptOrder: number;
    type: ReceiptTypeEnum;
    state: ReceiptStateEnum;
    amount: number;
    startDate: Date;
    endDate: Date;
  }

  export enum PackageEnum {
    Auto_Partance = 1,
    Auto_RisqueAgrave = 2,
    Auto_Swisslife = 3,
    Auto_AxaRisqueAggrave = 4,
    Auto_Axa110 = 5,
    Sante_InfinitySante = 200,
    Sante_Fidelity = 201,
    Sante_Acheel = 202,
    Rop_IjhCashback_Miltis = 1000,
    Filtrer = 9999,
  }

  export enum ProductEnum {
    Axa_Partance = 1,
    Mapfre_PanneMecanique = 2,
    Equite_RisqueAgrave = 3,
    IPA_Assistance = 4,
    SwissLife_600 = 5,
    Equite_Standard = 6,
    AxaRisqueAggrave_700 = 7,
    Auto_Axa110 = 8,
    Sante_Liberteo = 200,
    Sante_Fidelity = 201,
    Rop_IjhCashback_Miltis = 1000,
  }

  export interface FormulaWithFormGroup {
    formula: Formula;
    formGroup: FormGroup;
    codeFormule: string;
    produit: string;
  }

  export enum ComplementStateEnum {
    Active = 1,
    Paid = 2,
    Deleted = 3,
  }

  export interface VehicleDetails {
    dateMiseEnCirculation?: Date | undefined;
    dateAchatVehicule?: Date | undefined;
    marque?: string | undefined;
    modele?: string | undefined;
    energie?: string | undefined;
    chevauxFiscaux?: string | undefined;
    carrosserie?: string | undefined;
    version?: string | undefined;
    codeGta?: string | undefined;
    typeMine?: string | undefined;
    immatriculation?: string | undefined;
  }

  export interface Complement {
    id: number;
    creationDate: Date;
    comments: string;
    changes: PropertyChange<PolicyModels.PolicyChangeEnum>[];
    lastPayment: PaymentModels.PaymentTransactionInfos;
    amount: number;
    type: ComplementTypeEnum;
  }

  export interface ComplementTableRow {
    id: number;
    policyEffectDate: Date;
    amount: number;
    client: PolicyClient;
    policyId: number;
    type: ComplementTypeEnum;
    packageId: PackageEnum;
    insuranceType: CommonModels.InsuranceTypeEnum;
  }

  export enum ComplementTypeEnum {
    GarantieProvisoire = 1,
    Avenant = 2,
  }

  export interface CarPolicyActivation {
    firstClientPaymentPaid: number;
    complements: number[];
    totalClientPaid: number;
    documentStates: { [type: number]: DocumentModels.DocumentStateEnum };
    hasDifferencesWithDocuments: boolean;
    lastPolicyState: PolicyStateActivation;
    isPolicyActivatable: boolean;
  }

  export interface PolicyActivation {
    documents: PolicyActivationDocument[];
    creationDate: Date;
    effectDate: Date;
    isPolicyActivatable: boolean;
  }

  export interface PolicyActivationDocument {
    type: DocumentModels.DocumentType;
    state: DocumentModels.DocumentStateEnum;
  }

  export interface PolicyStateActivation {
    id: number;
    creationDate: Date;
    effectDate: Date;
    firstClientPayment: number;
    priceGap: number;
    car: VehicleDetails;
  }

  export interface PolicyTableRow {
    id: number;
    publicId: string;
    creator: string;
    insuranceType: CommonModels.InsuranceTypeEnum;
    client: PolicyClient;
    creationDate: Date;
    statusDate: Date;
    effectDate: Date;
    state: PolicyStateEnum;
    filesToHandle: number;
    documentStates: { [type: number]: DocumentModels.DocumentStateEnum };
  }

  export interface PoliciesPaginatedRequest
    extends TableModels.PaginatedRequest {
    search: string;
    policyState?: PolicyStateEnum;
    isSubscriptionMode: boolean;
    insuranceType?: CommonModels.InsuranceTypeEnum;
  }

  export interface PolicyInfo {
    policyId: number;
    clientId: number;
    client: PolicyClient;
  }

  export interface PolicyClient {
    civility: CommonModels.CivilityEnum;
    firstName: string;
    lastName: string;
    id: number;
  }

  export enum RenewalStateEnum {
    Rejected = 1,
    Accepted = 2,
    Canceled = 3,
  }

  export enum FinGarantieProvisoireTypeEnum {
    AnciennetePermisHorsChampSouscription = 1,
    AnnulationAdministrative = 2,
    AnnulationPermisConduire = 3,
    AucunePieceRecue = 4,
    CoefficientReductionMajorationHorsChampSouscription = 5,
    ComplementNonRegle = 6,
    DelitFuiteDeclare = 7,
    InvalidationPermisConduire = 8,
    AgeAssureHorsChampSouscription = 9,
    AbsenceCarteGrise = 10,
    AbsencePermis = 11,
    AbsenceRIB = 12,
    SouscripteurNonTitulaireCarteGrise = 13,
    PermisEtrangerNonTransforme = 14,
    PermisNonObtenuEnConduiteAccompagnee = 15,
    PertePermis = 16,
    ProfilRisqueIntarifable = 17,
    RefusObtempererNonDeclare = 18,
    ReleveInformationIncomplet = 19,
    SinistreHorsChampSouscription = 20,
    SinistreNonDeclare = 21,
    SuspensionPermisConduire = 22,
    UsageVehiculeExclu = 23,
    VéhiculeHorsChampSouscription = 24,
    ZoneCirculationExclue = 25,
    CessionVehicule = 26,
    NonTransformationDeLaCarteGrise = 27,
    AbsenceDePhotosDuVéhicule = 28,
  }

  export enum HelathPolicyChangeEnum {
    Product,
    ClientName,
    ClientAddress1,
    ClientAddress2,
    PolicyPublicId,
    PolicyEffectDate,
    Civility,
    Courtier,
    Email,
    Phone,
    Mobile,
    Formule,
    CreationDate,
    AssurePrincipalLastName,
    AssurePrincipalFirstName,
    ConjointLastName,
    ConjointFirstName,
    ConjointDateOfBirth,
    ConjointNumeroSecuriteSocial,
    ConjointRegimeSocial,
    Benef1LastName,
    Benef1FirstName,
    Benef1DateOfBirth,
    Benef1NumeroSecuriteSocial,
    Benef1RegimeSocial,
    Benef2LastName,
    Benef2FirstName,
    Benef2DateOfBirth,
    Benef2NumeroSecuriteSocial,
    Benef2RegimeSocial,
    Benef3LastName,
    Benef3FirstName,
    Benef3DateOfBirth,
    Benef3NumeroSecuriteSocial,
    Benef3RegimeSocial,
    Benef4LastName,
    Benef4FirstName,
    Benef4DateOfBirth,
    Benef4NumeroSecuriteSocial,
    Benef4RegimeSocial,
    AssurePrincipalDateOfBirth,
    AssurePrincipalNumeroSecuriteSocial,
    AssurePrincipalRegimeSocial,
    TerminationReason,
    TerminationEffectDate,
    TerminationCreationDate,
    InstanceReason,
    ContractStartDate,
  }

  export enum PolicyChangeEnum {
    FirstName = 1,
    LastName = 2,
    Email = 3,
    Phone = 4,
    Mobile = 5,
    Immatriculation = 6,
    CodeGta = 7,
    ParkingNuit = 8,
    ParkingJour = 9,
    ConducteurPrincipalBonus = 10,
    ConducteurPrincipalDateNaissance = 11,
    ConducteurPrincipalDatePermis = 12,
    ConducteurPrincipalTypePermis = 13,
    ConducteurPrincipalSituationFamiliale = 14,
    ConducteurPrincipalProfession = 15,
    ConducteurPrincipalNombreEnfants = 16,
    ConducteurPrincipalInterruptionAssurance = 17,
    ConducteurPrincipalNombreAnneeAssurance = 18,
    ConducteurPrincipalPeriodeAssurance = 19,
    ConducteurPrincipalRaisonResiliation = 20,
    Staggering = 21,
    TitulaireCarteGrise = 22,
    CodePostalPerso = 23,
    CodePostalPro = 24,
    AssureCivilite = 25,
    AssureAddress = 26,
    OptionnalWarranties = 27,
    Formula = 28,
    ConducteurPrincipalSinistres = 29,
    UsageDuVehicule = 30,
    Stationnement = 31,
    Financement = 32,
    KilometrageAnnuel = 33,
    VehiculeDejaAssure = 34,
    DateMiseEnCirculation = 35,
    DateAchat = 36,
    DateEffet = 37,
    DateSinistre1,
    DateSinistre2,
    DateSinistre3,
    ChargementCourtier,
    ContractStartDate = 42,
    ConducteurPrincipalnombreAnneeBonus50 = 43,
  }

  export interface PolicyRecap {
    id: number;
    publicId: string;
    insuranceType: CommonModels.InsuranceTypeEnum;
    packageId: PackageEnum;
    creationDate: Date;
    status: PolicyStateEnum;
    hasRenewal: boolean;
    quantieme?: number;
    isVad?: boolean;
    isRappel?: boolean;
    handledById: number;
    handledBy: string;
    handledByStartDate: Date;
    isDocumentJobAutoAssigned: boolean;
  }

  export enum PolicyStateEnum {
    GarantieProvisoire = 1,
    FinDeGarantieProvisoire = 2,
    EnCours = 3,
    MisEnDemeure = 4,
    Suspendue = 5,
    Resilie = 6,
    Traitement = 7,
    ActifAvecRejet = 8,
    TraitementAvenant = 9,
    Caducite = 10,
    Nullite = 11,
    ActifAttentePiece = 12,
    SansEffet = 13,
    ValidationEnAttente = 14,
  }

  export enum PolicyJobStatusEnum {
    Todo = 1,
    Controlled = 2,
    Instance = 3,
  }

  export enum InstanceReasonEnum {
    PriceIncrease = 1,
    RegimeChange,
    NoAddress,
    InconsistentSSN,
    EffectDate,
    NameInconsistency,
    Doublon,
  }

  export enum ReceiptTypeEnum {
    FraisDeDossier = 1,
    Acompte = 2,
    Quittance = 3,
    QuittanceDeRejet = 4,
    QuittanceDePerception = 5,
    QuittanceDeRemboursement = 6,
    QuittanceMoinsPercuComptant = 7,
    QuittanceTropPercuComptant = 8,
    QuittanceDeComplement = 9,
    QuittanceAnnulation = 10,
    FraisFinGarantieProvisoire = 11,
    QuittanceDeRemboursementSinistre = 12,
    QuittanceDeRegularisation = 13,
    QuittanceAnnulationRegularisation = 14,
    FraisDeDossierTunnelSante = 15,
  }

  export enum ReceiptStateEnum {
    Issued = 1,
    Paid = 2,
    Canceled = 3,
    Rejected = 4,
    Refunded = 5,
    PendingRefund = 6,
  }

  export enum BankRejectReasonEnum {
    IncorrectAccountNumber = 1,
    InvalidCreditorAccountNumber = 2,
    ClosedAccountNumber = 3,
    BlockedAccount = 4,
    InvalidDebtorAccountType = 5,
    InvalidAgent = 6,
    AccountDetailsChanged = 7,
    AccountInSequestration = 8,
    AccountInLiquidation = 9,
    TransactionForbidden = 10,
    InvalidBankOperationCode = 11,
    ZeroAmount = 12,
    NotAllowedAmount = 13,
    NotAllowedCurrency = 14,
    InsufficientFunds = 15,
    Duplication = 16,
    TooLowAmount = 17,
    BlockedAmount = 18,
    WrongAmount = 19,
    InvalidControlSum = 20,
    AlreadyReturnedTransaction = 21,
    InconsistenWithEndCustomer = 22,
    MissingCreditorAddress = 23,
    UnrecognisedInitiatingParty = 24,
    UnknownEndCustomer = 25,
    MissingDebtorAddress = 26,
    BankError = 27,
    AuthorisationCancelled = 28,
    CreditorBankIsNotRegistered = 29,
    IncorrectCurrency = 30,
    RequestedByCustomer = 31,
    DebtorBankIsNotRegistered = 32,
    ReturnForTechnicalReason = 33,
    InvalidDate = 34,
    CorrespondentBankNotPossible = 35,
    BalanceInfoRequest = 36,
    SettlementFailed = 37,
    EmvLiabilityShift = 38,
    EriOptionNotSupported = 39,
    InvalidFormat = 40,
    InvalidLocalInstrumentCode = 41,
    FollowingCancellationRequest = 42,
    Fraud = 43,
    FinalResponseMandateCancelled = 44,
    NoMandate = 45,
    MissingMandatoryInformationInMandate = 46,
    RefundRequestByEndCustomer = 47,
    EndCustomerDeceased = 48,
    NotSpecifiedReasonCustomerGenerated = 49,
    NotSpecifiedReasonAgentGenerated = 50,
    Narrative = 51,
    NoAnswerFromCustomer = 52,
    NotCompliant = 53,
    NoOriginalTransactionReceived = 54,
    PinLiabilityShift = 55,
    BankIdentifierIncorrect = 56,
    InvalidCreditorBicIdentifier = 57,
    NotUniqueTransactionReference = 58,
    MissingDebtorAccountOrIdentification = 59,
    MissingDebtorNameOrAddress = 60,
    MissingCreditorNameOrAddress = 61,
    RegulatoryReason = 62,
    ReturnUponUnableToApply = 63,
    SpecificServiceOfferedByDebtorAgent = 64,
    SpecificServiceOfferedByCreditorAgent = 65,
    CreditorNotOnWhitelistOfDebtor = 66,
    CreditorOnBlacklistOfDebtor = 67,
    MaximumNumberOfDirectDebitTransactionsExceeded = 68,
    MaximumDirectDebitTransactionAmountExceeded = 69,
    PaymentStopped = 70,
    PreviouslyStopped = 71,
    ServiceNotRendered = 72,
    CutOffTime = 73,
    RemovedFromTracking = 74,
    UnduePayment = 75,
    UnknownReason = 76,
  }

  export interface CarPolicyPricingMetadatas extends PolicyPricingMetadatas {
    formulas: { [formula: number]: CarFormulaMetadata };
  }

  export interface PolicyPricingMetadatas {
    commission: CommonModels.MinMaxMetric;
    management: CommonModels.MinMaxMetric;
    selectedFees: number;
    selectedLoading: number;
    selectedFormula: number;
    selectedOptionnalWarranties: number[];
    clientPrice: QuotationModels.Staggered<number>;
    brokerCommissions: QuotationModels.Staggered<number>;
    staggering?: QuotationModels.QuittancementEnum;
    commissionType: QuotationModels.CommissionTypeEnum;
    listeReponseAfi: QuotationModels.ReponseAfiExport[];
  }

  export interface PolicyPricing {
    formulas: FormulaDictionary;
    clientPrice: QuotationModels.Staggered<number>;
    brokerCommissions: QuotationModels.Staggered<number>;
  }

  export interface FinGarantieProvisoireRequest {
    reasonId?: number;
    effectiveDate?: string;
  }

  export interface CarFormulaMetadata extends FormulaMetadata {
    warranties: Warranty[];
  }

  // tslint:disable-next-line:no-empty-interface
  export interface FormulaMetadata extends Formula {}

  export interface Formula {
    formula: QuotationModels.FormulaEnum;
    pricing: QuotationModels.Pricing;
    optionalWarranties: { [warranty: number]: OptionalWarranty };
  }

  export interface OptionalWarranty {
    warranty: QuotationModels.WarrantyEnum;
    pricing: QuotationModels.Pricing;
  }

  export interface Warranty {
    warranty: number;
    franchise?: any;
  }

  export interface UpdatePolicyResult {
    hasChanges: boolean;
    priceGap?: number;
    hasNoPricing: boolean;
  }

  export interface CreateComplementForm {
    comments: string;
    sendEmail: boolean;
    changes: {
      property: PolicyModels.PolicyChangeEnum;
      checked: boolean;
      oldValue: string;
      newValue: string;
    }[];
  }

  export interface ReceiptTableRow {
    id: number;
    receiptNumber: number;
    order: number;
    type: ReceiptTypeEnum;
    startDate: Date;
    endDate: Date;
    amount: number;
    state: ReceiptStateEnum;
    paymentDate: Date;
    effectivePaymentDate?: Date;
    parentId: number;
    grandParentId: number;
    hasAttemptTax: boolean;
    coeff: number;
    hasRejectionFees: boolean;
    isRemovingFees: boolean;
    rejectDetails: RejectDetails;
    comment: string;
  }

  export interface RejectDetails {
    date: Date;
    reason: PolicyModels.BankRejectReasonEnum;
    debtorName: string;
    iban: string;
    bic: string;
  }

  export interface UnpaidReceipts {
    receipts: UnpaidReceipt[];
    total: number;
    lastPayment: PaymentModels.PaymentTransactionInfos;
  }

  export interface UnpaidReceiptsTableRow {
    total: number;
    receiptCount: number;
    policyId: number;
    packageId: PackageEnum;
    insuranceType: CommonModels.InsuranceTypeEnum;
    client: PolicyClient;
    warning: UnpaidReceiptsWarning;
    paymentOrder?: PaymentModels.PaymentOrderResume;
  }

  export interface UnpaidReceiptsWarning {
    daysLeft: number;
    policyStateIncoming: PolicyModels.PolicyStateEnum;
  }

  export interface UnpaidReceipt {
    id: number;
    receiptNumber: number;
    startDate: Date;
    endDate: Date;
    amount: number;
  }

  export enum CaduciteTypeEnum {
    AnciennetePermisHorsChampSouscription = 1,
    AnnulationAdministrative = 2,
    AnnulationPermisConduire = 3,
    AucunePieceRecue = 4,
    CoefficientReductionMajorationHorsChampSouscription = 5,
    ComplementNonRegle = 6,
    DelitFuiteDeclare = 7,
    InvalidationPermisConduire = 8,
    AgeAssureHorsChampSouscription = 9,
    AbsenceCarteGrise = 10,
    AbsencePermis = 11,
    AbsenceRIB = 12,
    SouscripteurNonTitulaireCarteGrise = 13,
    PermisEtrangerNonTransforme = 14,
    PermisNonObtenuEnConduiteAccompagnee = 15,
    PertePermis = 16,
    ProfilRisqueIntarifable = 17,
    RefusObtempererNonDeclare = 18,
    ReleveInformationIncomplet = 19,
    SinistreHorsChampSouscription = 20,
    SinistreNonDeclare = 21,
    SuspensionPermisConduire = 22,
    UsageVehiculeExclu = 23,
    VéhiculeHorsChampSouscription = 24,
    ZoneCirculationExclue = 25,
  }

  export enum NulliteTypeEnum {
    AnciennetePermisHorsChampSouscription = 1,
    AnnulationAdministrative = 2,
    AnnulationPermisConduire = 3,
    AucunePieceRecue = 4,
    CoefficientReductionMajorationHorsChampSouscription = 5,
    ComplementNonRegle = 6,
    DelitFuiteDeclare = 7,
    InvalidationPermisConduire = 8,
    AgeAssureHorsChampSouscription = 9,
    AbsenceCarteGrise = 10,
    AbsencePermis = 11,
    AbsenceRIB = 12,
    SouscripteurNonTitulaireCarteGrise = 13,
    PermisEtrangerNonTransforme = 14,
    PermisNonObtenuEnConduiteAccompagnee = 15,
    PertePermis = 16,
    ProfilRisqueIntarifable = 17,
    RefusObtempererNonDeclare = 18,
    ReleveInformationIncomplet = 19,
    SinistreHorsChampSouscription = 20,
    SinistreNonDeclare = 21,
    SuspensionPermisConduire = 22,
    UsageVehiculeExclu = 23,
    VéhiculeHorsChampSouscription = 24,
    ZoneCirculationExclue = 25,
  }

  export enum TerminationTypeEnum {
    Hamon = 1,
    AtCompletion = 2,
    CarDestroyedOrSold = 3,
    SituationChanged = 4,
    RefusDeRevalorisation = 5,
    Misc = 6,
    Npp = 7,
    ChangementDeRisque,
    ChangementDeSituationFamiliale,
    Amiable,
    Augmentation,
    Death,
    Ria,
    ErreurDeSaisie,
    Doublon,
    DemandeDuClient,
    DemandeDuCourtier,
    Report,
    AggravationRisque,
    Résiliationaprèssinistre,
    Résiliationaggravationdurisque,
    RésiliationéchéanceAssureur,
    Nullité,
  }

  export enum SituationChangeEnum {
    ChangementDomicile = 1,
    ChangementRegimeMatrimonial = 2,
    ChangementSituationMatrimoniale = 3,
    ChangementProfession = 4,
    Retraite = 5,
    Autre = 6,
  }

  export interface FormulaMetadataDictionary {
    [formula: number]: FormulaMetadata;
  }

  export interface FormulaDictionary {
    [formula: number]: Formula;
  }

  export interface OptionalWarrantyDictionary {
    [warranty: number]: OptionalWarranty;
  }
}

export enum PolicyStatePickerEnum {
  LastState = 1,
  EffectiveState = 2,
}

export enum SubStatus {
  ERROR,
  INPUT_ERROR,
  CUSTOMER_REQUEST,
  SUPPLIER_REQUEST,
  RETAILER_REQUEST,
  DUPLICATE,
  NON_PAYMENT,
  CLAIM,
  BARGAIN_CHANGE,
  HOUSE_MOVING,
  MUTUAL_MANDATORY,
  OTHERS,
  SUPPLIER_DUE,
  CUSTOMER_DUE,
  MARITAL_STATUS_CHANGE,
  CUSTOMER_REQUEST_TERMINATE_OTHERS_CONTRACTS,
  AGREEMENT,
  MISSTATEMENT,
  MISSING_DOCUMENTARY_EVIDENCE,
  SELL_VEHICLE,
  BENEFICIARY_CMU,
  RATING_INCREASE,
  RISK_OUT,
  MULTIPLE_PAYMENT_INCIDENTS,
  WRONG_DECLARATION,
  LAW_HAMON,
  CMU,
  ACS,
  DEATH,
  MIGRATION,
  RETIREMENT,
  REPORT,
  SUB_ANNUAL_TERMINATION,
  ACTIVITY_TERMINATE,
  LACK_OF_STAFF,
}

export enum BankRejectReasons {
  IncorrectAccountNumber = 1,
  InvalidCreditorAccountNumber,
  ClosedAccountNumber,
  BlockedAccount,
  InvalidDebtorAccountType,
  InvalidAgent,
  AccountDetailsChanged,
  AccountInSequestration,
  AccountInLiquidation,
  TransactionForbidden,
  InvalidBankOperationCode,
  ZeroAmount,
  NotAllowedAmount,
  NotAllowedCurrency,
  InsufficientFunds,
  Duplication,
  TooLowAmount,
  BlockedAmount,
  WrongAmount,
  InvalidControlSum,
  AlreadyReturnedTransaction,
  InconsistenWithEndCustomer,
  MissingCreditorAddress,
  UnrecognisedInitiatingParty,
  UnknownEndCustomer,
  MissingDebtorAddress,
  BankError,
  AuthorisationCancelled,
  CreditorBankIsNotRegistered,
  IncorrectCurrency,
  RequestedByCustomer,
  DebtorBankIsNotRegistered,
  ReturnForTechnicalReason,
  InvalidDate,
  CorrespondentBankNotPossible,
  BalanceInfoRequest,
  SettlementFailed,
  EmvLiabilityShift,
  EriOptionNotSupported,
  InvalidFormat,
  InvalidLocalInstrumentCode,
  FollowingCancellationRequest,
  Fraud,
  FinalResponseMandateCancelled,
  NoMandate,
  MissingMandatoryInformationInMandate,
  RefundRequestByEndCustomer,
  EndCustomerDeceased,
  NotSpecifiedReasonCustomerGenerated,
  NotSpecifiedReasonAgentGenerated,
  Narrative,
  NoAnswerFromCustomer,
  NotCompliant,
  NoOriginalTransactionReceived,
  PinLiabilityShift,
  BankIdentifierIncorrect,
  InvalidCreditorBicIdentifier,
  NotUniqueTransactionReference,
  MissingDebtorAccountOrIdentification,
  MissingDebtorNameOrAddress,
  MissingCreditorNameOrAddress,
  RegulatoryReason,
  ReturnUponUnableToApply,
  SpecificServiceOfferedByDebtorAgent,
  SpecificServiceOfferedByCreditorAgent,
  CreditorNotOnWhitelistOfDebtor,
  CreditorOnBlacklistOfDebtor,
  MaximumNumberOfDirectDebitTransactionsExceeded,
  MaximumDirectDebitTransactionAmountExceeded,
  PaymentStopped,
  PreviouslyStopped,
  ServiceNotRendered,
  CutOffTime,
  RemovedFromTracking,
  UnduePayment,
  UnknownReason,
}

export interface ReponseAfiExport {
  IsDoublon?: string;
  Tarifs?: Tarif[];
  tarifsTout?: Tarif[];
}

export interface Tarif {
  codeFormule?: string;
  iD_Cotation?: string;
  iD_Formule?: string;
  isComplet?: string;
  montant?: string;
  ordre?: string;
  produitTarif?: string;
  urlSouscription?: string;
  nomFormule?: string;
  prix?: number;
}

// public class ReponseAfiExport
// {
// 	public bool IsDoublon { get; set; }

// 	public List<Tarif> Tarifs { get; set; }
// }

// public class Tarif
// {

// 	public string CodeFormule { get; set; }

// 	public int ID_Cotation { get; set; }

// 	public int ID_Formule { get; set; }

// 	public bool IsComplet { get; set; }

// 	public decimal Montant { get; set; }

// 	public int Ordre { get; set; }

// }
